<template>
  <div class="topicCard">
    <div class="topicCard__thumbnail"
         :class="{ 'topicCard__thumbnail__alt': altCardStyle }"
         @click="onCtaHandler">
      <div class="topicCard__thumbnail__status"
           :class="{ 'd-none': altCardStyle }">
        <progress-icon size="1x"
                       :status="topicStatus"></progress-icon>
      </div>
      <div class="topicCard__thumbnail__overlay"></div>
      <img v-if="thumbnail"
           :src="thumbnail"
           :alt="title">
      <div v-else
           class="topicCard__thumbnail__placeholder"></div>
    </div>
    <div class="topicCard__content">
      <h4 class="topicCard__content--title">
        {{ title }}
      </h4>
      <p class="topicCard__content--description">
        {{ searchText }}
      </p>
    </div>
    <b-alert :show="ui.topic.error"
             variant="danger"
             class="alert m-auto">
      {{ ui.topic.errorMessage }}
    </b-alert>
    <loading-spinner v-if="ui.topic.loading"
                     class="my-5 mx-auto navigate-module__loader"
                     size="4x">
    </loading-spinner>
    <div v-if="ui.topic.loading === false && ui.topic.error === false"
         class="topicCard__cta__container">
      <template v-if="altCardStyle">
        <a class="topicCard__cta__alt"
           @click="onCtaHandler">
          <span class="mx-1">{{ topicLink.label }}</span>
          <font-awesome-icon icon="chevron-right"
                             size="xs"></font-awesome-icon>
        </a>
      </template>
      <template v-else>
        <button class="topicCard__cta cta cta--primary cta--slim"
                @click="onCtaHandler">
          {{ topicLink.label }}
        </button>
      </template>
    </div>
  </div>
</template>
<script>

import { TOPIC_STATUS } from "~/Components/constants";
import {
  getTopicUrl,
  getTopicUserStatus,
} from "~/Scripts/resources/topicResource";

import ProgressIcon from "~/Components/Common/ProgressIcon.vue";

export default {
  name: "TopicCard",
  components: {
    ProgressIcon,
  },
  props: {
    topicId: {
      type: Number,
      default: undefined,
      required: true,
    },
    status: {
      type: String,
      default: undefined,
    },
    thumbnail: {
      type: String,
      default: "",
    },
    title: {
      type: String,
      default: "",
    },
    searchText: {
      type: String,
      default: "",
    },
    overrideLink: {
      type: Object,
      default: () => ( { label: null, location: null } ),
      validator ( val ) {
        return Object.prototype.hasOwnProperty.call( val, "label" )
          && Object.prototype.hasOwnProperty.call( val, "location" );
      },
    },
    altCardStyle: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ( {
    url: "#",
    topicStatus: TOPIC_STATUS.NOT_STARTED,
    ui: {
      topicLink: {
        label: {
          [TOPIC_STATUS.NOT_STARTED]: "Access",
          [TOPIC_STATUS.STARTED]: "Access",
          [TOPIC_STATUS.COMPLETED]: "Recap topic",
          [TOPIC_STATUS.MASTERED]: "Revisit topic",
        },
      },
      topic: {
        loading: false,
        error: false,
        errorMessage: "No link found matching the topic ID",
      },
    },
  } ),
  computed: {
    topicLink () {
      if ( this.overrideLink.label == null ) {
        return { label: this.ui.topicLink.label[this.topicStatus], location: this.url };
      }
      return this.overrideLink;
    },
  },
  mounted () {
    if ( this.overrideLink.location == null ) {
      this.getTopicContentLocation();
    }

    if ( this.status == null ) {
      getTopicUserStatus( this.topicId ).then( response => {
        const { data } = response;
        this.topicStatus = data;
      } );
    } else {
      this.topicStatus = this.status;
    }
  },
  methods: {
    onCtaHandler () {
      window.location.href = this.topicLink.location;
    },
    getTopicContentLocation () {
      this.ui.topic.loading = true;

      getTopicUrl( this.topicId ).then( response => {
        const { status, data } = response;

        if ( status === 200 && data ) {
          this.url = data;
        } else {
          throw new Error( `No link found for Topic with ID: ${this.topicId}` );
        }
      } ).catch( ex => {
        console.error( ex );
        this.ui.topic.error = true;
        this.ui.topic.errorMessage = ex;
      } ).finally( () => {
        this.ui.topic.loading = false;
      } );
    },
  },
};
</script>

<style lang="less" scoped>
  @import "../../Styles/utils/index.less";
  @import "../../Styles/common/buttons.less";

  .topicCard {
    padding: 0 0 56px;
    color: @white;
    margin: 0 0 32px 0;
    position: relative;
    display: flex;
    flex-flow: column;

    &__content {
      margin: 16px 0 20px;
      flex: 1;

      &--title {
        color: @white;
        margin: 2px 0;
        font-weight: 700;
        font-size: 16px;
      }

      &--description {
        margin: 0;
        font-weight: 200;
        font-size: 16px;
        color: @white;
      }
    }

    &__cta {
      display: flex;
      justify-content: center;
      align-items: center;

      &{}

      &__alt {
        text-decoration: none;
        color: @white;
        font-size: 16px;
        cursor: pointer;

        &{}

        &:hover {
          color: darken( @white, 20% );
        }
      }

      &__container {
        position: absolute;
        bottom: 8px;
      }
    }

    &__thumbnail {
      width: 100%;
      position: relative;
      cursor: pointer;
      min-height: 123px;
      overflow: hidden;
      .border-radius( 4px );

      &{}

      &__alt {
        overflow: unset;
        border: 1px solid @blue;
      }

      &__status {
        position: absolute;
        top: 12px;
        right: 12px;
        .border-radius( 50% );
        z-index: 20;
        display: flex;
        justify-content: center;
        align-items: center;
      }

      &__overlay {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        background: transparent;
        z-index: 10;

        &:hover {
          background: @white;
          .opacity( 0.3 );
        }
      }

      &__placeholder {
        width: 100%;
        height: 123px;
        background: @idle-gray;
      }

      img {
        vertical-align: middle;
        object-fit: cover;
        width: 100%;
      }

    }
  }
</style>
